/** True when an oRPC client error carries `code`, at the top level or nested under `data`. */
export function hasOrpcCode(err: unknown, code: string): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; data?: { code?: unknown } };
  return e.code === code || e.data?.code === code;
}

/** True when an oRPC client error is a NOT_FOUND response. */
export function isOrpcNotFoundError(err: unknown): boolean {
  return hasOrpcCode(err, 'NOT_FOUND');
}

/** True when an oRPC client error is a FORBIDDEN response. */
export function isOrpcForbiddenError(err: unknown): boolean {
  return hasOrpcCode(err, 'FORBIDDEN');
}

/** True when an oRPC client error is a CONFLICT response (e.g. a duplicate name). */
export function isOrpcConflictError(err: unknown): boolean {
  return hasOrpcCode(err, 'CONFLICT');
}

/**
 * The platform's own explanation, when the server put it under `data.message` rather than the
 * top-level `message`. That split is deliberate on any error whose reason can carry customer text (a
 * chosen account name, a resource id): the top-level `message` reaches the log aggregator through
 * pino's error serializer unredacted, while `data.message` is on `lib/logger.ts`'s redact list. So the
 * top-level field is a fixed generic string and this is where the "why" actually lives — read it
 * first, and fall back to `message` for every error that has no such split.
 */
export function orpcErrorDetail(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return undefined;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.length > 0 ? message : undefined;
}

/** `data.message` when the server supplied one, else the error's own message, else `fallback`. */
export function orpcErrorMessage(err: unknown, fallback: string): string {
  return orpcErrorDetail(err) ?? (err instanceof Error && err.message ? err.message : fallback);
}
